import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bulletin, BulletinCategory, BulletinMediaItem, TASK_STATUS_MAP } from './AdminBulletinPage';

const CATEGORY_MAP: Record<BulletinCategory, { label: string; icon: string; color: string; bg: string }> = {
  thong_bao: { label: 'Thông báo', icon: 'fas fa-bullhorn', color: '#8d2a2c', bg: 'rgba(141, 42, 44, 0.09)' },
  huong_dan: { label: 'Hướng dẫn / SOP', icon: 'fas fa-book-open', color: '#1e7e48', bg: 'rgba(30, 126, 72, 0.1)' },
  giao_viec: { label: 'Nhiệm vụ / Task', icon: 'fas fa-tasks', color: '#2980b9', bg: 'rgba(41, 128, 185, 0.12)' },
  vi_pham: { label: 'Vi phạm / Nhắc nhở', icon: 'fas fa-exclamation-triangle', color: '#c0392b', bg: 'rgba(192, 57, 43, 0.1)' },
  khen_thuong: { label: 'Khen thưởng / Vinh danh', icon: 'fas fa-award', color: '#b7791f', bg: 'rgba(183, 121, 31, 0.12)' },
  khan_cap: { label: 'Khẩn cấp', icon: 'fas fa-bolt', color: '#781d20', bg: 'rgba(120, 29, 32, 0.15)' },
};

export const BulletinBoardPage: React.FC = () => {
  const { user } = useAuth();
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter and tabs
  const [activeTab, setActiveTab] = useState<string>('all'); // 'all', 'unread', or category
  const [searchKeyword, setSearchKeyword] = useState('');

  // Detail Modal state
  const [selectedBulletin, setSelectedBulletin] = useState<Bulletin | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAckSubmitting, setIsAckSubmitting] = useState(false);

  // Task claim & completion state
  const [taskActionLoadingId, setTaskActionLoadingId] = useState<string | null>(null);
  const [completionModalBulletin, setCompletionModalBulletin] = useState<Bulletin | null>(null);
  const [completionNote, setCompletionNote] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);

  // Lightbox state for images
  const [lightboxImages, setLightboxImages] = useState<BulletinMediaItem[]>([]);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState<number | null>(null);

  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [user?.id]);

  // Fetch bulletins for staff
  const loadBulletins = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (activeTab !== 'all' && activeTab !== 'unread') {
        params.append('category', activeTab);
      }
      if (activeTab === 'unread') {
        params.append('unreadOnly', 'true');
      }
      if (searchKeyword.trim()) {
        params.append('search', searchKeyword.trim());
      }

      const res = await fetch(`/api/bulletins?${params.toString()}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể tải bảng tin');
      }
      setBulletins(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Lỗi khi tải bảng tin');
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, activeTab, searchKeyword]);

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  // Open detail & mark as read
  const handleOpenDetail = async (bulletin: Bulletin) => {
    setSelectedBulletin(bulletin);
    setIsDetailOpen(true);

    // Auto mark as read if not read yet
    if (!bulletin.is_read) {
      try {
        const res = await fetch(`/api/bulletins/${bulletin.id}/read`, {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ acknowledge: false }),
        });
        const data = await res.json();
        if (data.success) {
          // Update local state
          setBulletins((prev) =>
            prev.map((b) => (b.id === bulletin.id ? { ...b, is_read: true, read_at: data.data.read_at } : b))
          );
          setSelectedBulletin((curr) =>
            curr && curr.id === bulletin.id ? { ...curr, is_read: true, read_at: data.data.read_at } : curr
          );
        }
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }
  };

  // Handle acknowledge
  const handleAcknowledge = async () => {
    if (!selectedBulletin) return;
    try {
      setIsAckSubmitting(true);
      const res = await fetch(`/api/bulletins/${selectedBulletin.id}/read`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ acknowledge: true }),
      });
      const data = await res.json();
      if (data.success) {
        setBulletins((prev) =>
          prev.map((b) =>
            b.id === selectedBulletin.id
              ? {
                  ...b,
                  is_read: true,
                  read_at: data.data.read_at,
                  is_acknowledged: true,
                  acknowledged_at: data.data.acknowledged_at,
                }
              : b
          )
        );
        setSelectedBulletin((curr) =>
          curr
            ? {
                ...curr,
                is_read: true,
                read_at: data.data.read_at,
                is_acknowledged: true,
                acknowledged_at: data.data.acknowledged_at,
              }
            : null
        );
      } else {
        alert(data.error || 'Không thể xác nhận');
      }
    } catch {
      alert('Lỗi kết nối khi xác nhận');
    } finally {
      setIsAckSubmitting(false);
    }
  };

  // Open Lightbox
  const handleOpenLightbox = (images: BulletinMediaItem[], index: number) => {
    setLightboxImages(images);
    setActiveLightboxIndex(index);
  };

  const handleNextLightbox = () => {
    if (activeLightboxIndex !== null && activeLightboxIndex < lightboxImages.length - 1) {
      setActiveLightboxIndex(activeLightboxIndex + 1);
    }
  };

  const handlePrevLightbox = () => {
    if (activeLightboxIndex !== null && activeLightboxIndex > 0) {
      setActiveLightboxIndex(activeLightboxIndex - 1);
    }
  };

  // Claim Task
  const handleClaimTask = async (bulletin: Bulletin, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Bạn muốn nhận đảm nhiệm công việc: "${bulletin.title}"?`)) {
      return;
    }
    try {
      setTaskActionLoadingId(bulletin.id);
      const res = await fetch(`/api/bulletins/${bulletin.id}/claim-task`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể nhận nhiệm vụ');
      }
      const updated = data.data;
      setBulletins((prev) =>
        prev.map((b) => (b.id === bulletin.id ? { ...b, ...updated } : b))
      );
      setSelectedBulletin((curr) =>
        curr && curr.id === bulletin.id ? { ...curr, ...updated } : curr
      );
      alert('Đã nhận nhiệm vụ thành công! Vui lòng hoàn thành theo tiến độ.');
    } catch (err: any) {
      alert(err.message || 'Lỗi khi nhận việc');
    } finally {
      setTaskActionLoadingId(null);
    }
  };

  // Unclaim Task
  const handleUnclaimTask = async (bulletin: Bulletin, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Bạn có chắc muốn trả lại nhiệm vụ "${bulletin.title}" về trạng thái chờ nhận?`)) {
      return;
    }
    try {
      setTaskActionLoadingId(bulletin.id);
      const res = await fetch(`/api/bulletins/${bulletin.id}/unclaim-task`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể trả lại nhiệm vụ');
      }
      const updated = data.data;
      setBulletins((prev) =>
        prev.map((b) => (b.id === bulletin.id ? { ...b, ...updated } : b))
      );
      setSelectedBulletin((curr) =>
        curr && curr.id === bulletin.id ? { ...curr, ...updated } : curr
      );
      alert('Đã trả lại nhiệm vụ thành công.');
    } catch (err: any) {
      alert(err.message || 'Lỗi khi trả lại việc');
    } finally {
      setTaskActionLoadingId(null);
    }
  };

  // Open Complete Modal
  const handleOpenCompleteModal = (bulletin: Bulletin, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCompletionModalBulletin(bulletin);
    setCompletionNote('');
  };

  // Submit Complete Task
  const handleSubmitCompleteTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completionModalBulletin) return;
    try {
      setIsCompleting(true);
      const res = await fetch(`/api/bulletins/${completionModalBulletin.id}/complete-task`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ completion_note: completionNote.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể báo cáo hoàn thành');
      }
      const updated = data.data;
      setBulletins((prev) =>
        prev.map((b) => (b.id === completionModalBulletin.id ? { ...b, ...updated } : b))
      );
      setSelectedBulletin((curr) =>
        curr && curr.id === completionModalBulletin.id ? { ...curr, ...updated } : curr
      );
      setCompletionModalBulletin(null);
      alert('Đã báo cáo hoàn thành nhiệm vụ thành công!');
    } catch (err: any) {
      alert(err.message || 'Lỗi khi báo cáo hoàn thành');
    } finally {
      setIsCompleting(false);
    }
  };

  // Unread count
  const unreadCount = useMemo(() => {
    return bulletins.filter((b) => !b.is_read).length;
  }, [bulletins]);

  return (
    <div className="bulletin-board-container">
      {/* Top Banner */}
      <div className="bulletin-board-header">
        <div className="header-text">
          <h1>Bảng tin & Thông báo nội bộ</h1>
          <p>Thông báo quan trọng, quy trình hướng dẫn và phân công nhiệm vụ</p>
        </div>

        {/* Search */}
        <div className="header-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Tìm kiếm thông tin..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
          {searchKeyword && (
            <button className="btn-clear" onClick={() => setSearchKeyword('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bulletin-tabs-bar">
        <button
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          Tất cả
        </button>

        <button
          className={`tab-btn ${activeTab === 'unread' ? 'active' : ''}`}
          onClick={() => setActiveTab('unread')}
        >
          Chưa đọc
          {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>

        <button
          className={`tab-btn ${activeTab === 'thong_bao' ? 'active' : ''}`}
          onClick={() => setActiveTab('thong_bao')}
        >
          Thông báo
        </button>

        <button
          className={`tab-btn ${activeTab === 'huong_dan' ? 'active' : ''}`}
          onClick={() => setActiveTab('huong_dan')}
        >
          Hướng dẫn SOP
        </button>

        <button
          className={`tab-btn ${activeTab === 'giao_viec' ? 'active' : ''}`}
          onClick={() => setActiveTab('giao_viec')}
        >
          Nhiệm vụ
        </button>

        <button
          className={`tab-btn ${activeTab === 'vi_pham' ? 'active' : ''}`}
          onClick={() => setActiveTab('vi_pham')}
        >
          Vi phạm
        </button>

        <button
          className={`tab-btn ${activeTab === 'khen_thuong' ? 'active' : ''}`}
          onClick={() => setActiveTab('khen_thuong')}
        >
          Khen thưởng
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="bulletin-state-box">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#8d2a2c' }}></i>
          <p>Đang tải thông tin...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bulletin-error-banner">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={loadBulletins}>Thử lại</button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && bulletins.length === 0 && (
        <div className="bulletin-state-box">
          <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#1e7e48', marginBottom: '12px' }}></i>
          <h3>Không có thông báo mới</h3>
          <p>Bạn đã xem hết các thông tin trong mục này hoặc chưa có thông báo mới nào.</p>
        </div>
      )}

      {/* Cards Grid */}
      {!isLoading && bulletins.length > 0 && (
        <div className="bulletin-cards-grid">
          {bulletins.map((item) => {
            const catInfo = CATEGORY_MAP[item.category] || CATEGORY_MAP.thong_bao;
            const images = (item.media_files || []).filter((m) => m.mimeType.startsWith('image/'));
            const videos = (item.media_files || []).filter((m) => m.mimeType.startsWith('video/'));
            const hasThumbnail = images.length > 0;
            const isUnread = !item.is_read;

            return (
              <div
                key={item.id}
                className={`bulletin-user-card ${isUnread ? 'is-unread' : ''} ${item.is_pinned ? 'is-pinned' : ''}`}
                onClick={() => handleOpenDetail(item)}
              >
                {/* Thumbnail header if image exists */}
                {hasThumbnail && (
                  <div className="card-thumb-container">
                    <img src={images[0].url} alt={item.title} className="card-thumb-img" loading="lazy" />
                    {images.length > 1 && (
                      <span className="thumb-count-badge">
                        <i className="fas fa-images"></i> +{images.length - 1}
                      </span>
                    )}
                  </div>
                )}

                <div className="card-inner-body">
                  {/* Category & Tag row */}
                  <div className="card-tag-row">
                    <span className="cat-chip" style={{ color: catInfo.color, background: catInfo.bg }}>
                      <i className={catInfo.icon}></i> {catInfo.label}
                    </span>

                    {item.priority === 'urgent' && (
                      <span className="tag-urgent">
                        <i className="fas fa-bolt"></i> Khẩn cấp
                      </span>
                    )}

                    {item.target_type === 'department' && (
                      <span className="tag-dept" title="Dành cho bộ phận của bạn">
                        <i className="fas fa-building"></i> {item.target_department}
                      </span>
                    )}

                    {item.target_type === 'specific' && (
                      <span className="tag-direct" title="Thông tin được gửi riêng cho bạn">
                        <i className="fas fa-user-tag"></i> Dành cho bạn
                      </span>
                    )}

                    {item.category === 'giao_viec' && item.task_status && (
                      <span className={`task-status-badge status-${item.task_status}`}>
                        <i className={TASK_STATUS_MAP[item.task_status]?.icon || 'fas fa-tasks'}></i>{' '}
                        {TASK_STATUS_MAP[item.task_status]?.label}
                      </span>
                    )}

                    {item.is_pinned && (
                      <span className="tag-pinned" title="Tin ghim quan trọng">
                        <i className="fas fa-thumbtack"></i>
                      </span>
                    )}

                    {isUnread && <span className="tag-new">Mới</span>}
                  </div>

                  {/* Title */}
                  <h3 className="card-headline">{item.title}</h3>

                  {/* Summary */}
                  {item.summary && <p className="card-excerpt">{item.summary}</p>}

                  {/* Task Action & Status Panel (for giao_viec) */}
                  {item.category === 'giao_viec' && (
                    <div className="card-task-panel" onClick={(e) => e.stopPropagation()}>
                      {item.due_date && (
                        <div className="card-task-due">
                          <i className="fas fa-calendar-check"></i>
                          <span>Hạn chót: <strong>{new Date(item.due_date).toLocaleString('vi-VN')}</strong></span>
                        </div>
                      )}

                      {item.task_status === 'pending' && (
                        <div className="task-action-row">
                          <span className="task-sub-status text-amber">
                            Chưa ai nhận
                          </span>
                          <button
                            className="btn-claim-task"
                            disabled={taskActionLoadingId === item.id}
                            onClick={(e) => handleClaimTask(item, e)}
                          >
                            {taskActionLoadingId === item.id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : null}
                            <span>Nhận việc</span>
                          </button>
                        </div>
                      )}

                      {item.task_status === 'in_progress' && (
                        <div className="task-action-row">
                          {item.claimed_by_id === user?.id ? (
                            <div className="task-user-assigned-box">
                              <div className="task-my-badge">
                                Đang đảm nhiệm
                              </div>
                              <div className="task-btn-row">
                                <button
                                  className="btn-complete-task"
                                  onClick={(e) => handleOpenCompleteModal(item, e)}
                                >
                                  Báo cáo hoàn thành
                                </button>
                                <button
                                  className="btn-unclaim-task"
                                  disabled={taskActionLoadingId === item.id}
                                  onClick={(e) => handleUnclaimTask(item, e)}
                                  title="Trả lại việc"
                                >
                                  Trả lại
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="task-other-assigned-box">
                              Đang thực hiện: <strong>{item.claimed_by_name || 'Đồng nghiệp'}</strong>
                            </div>
                          )}
                        </div>
                      )}

                      {item.task_status === 'completed' && (
                        <div className="task-completed-box">
                          <div className="task-done-title">
                            <i className="fas fa-check-circle"></i> Đã xong bởi <strong>{item.claimed_by_name || 'Nhân viên'}</strong>
                          </div>
                          {item.completion_note && (
                            <p className="task-done-note">"{item.completion_note}"</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bottom Meta */}
                  <div className="card-footer-meta">
                    <div className="footer-meta-left">
                      <span>
                        <i className="fas fa-user-circle"></i> {item.author_name}
                      </span>
                      <span>
                        <i className="fas fa-clock"></i> {new Date(item.created_at).toLocaleDateString('vi-VN')}
                      </span>
                    </div>

                    <div className="footer-meta-right">
                      {videos.length > 0 && (
                        <span className="icon-indicator" title="Có video">
                          <i className="fas fa-play-circle"></i>
                        </span>
                      )}
                      {item.require_ack && (
                        <span
                          className={`icon-indicator ${item.is_acknowledged ? 'ack-done' : 'ack-pending'}`}
                          title={item.is_acknowledged ? 'Đã xác nhận' : 'Cần bấm xác nhận'}
                        >
                          <i className="fas fa-signature"></i>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DETAIL MODAL */}
      {isDetailOpen && selectedBulletin && (
        <div className="bulletin-modal-backdrop" onClick={() => setIsDetailOpen(false)}>
          <div className="bulletin-modal-window user-detail-window" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <div className="modal-header-tags">
                <span
                  className="cat-chip"
                  style={{
                    color: CATEGORY_MAP[selectedBulletin.category]?.color,
                    background: CATEGORY_MAP[selectedBulletin.category]?.bg,
                  }}
                >
                  <i className={CATEGORY_MAP[selectedBulletin.category]?.icon}></i>{' '}
                  {CATEGORY_MAP[selectedBulletin.category]?.label}
                </span>

                {selectedBulletin.priority === 'urgent' && (
                  <span className="tag-urgent">
                    <i className="fas fa-bolt"></i> Khẩn cấp
                  </span>
                )}

                {selectedBulletin.target_type === 'department' && (
                  <span className="tag-dept" title="Dành cho bộ phận">
                    <i className="fas fa-building"></i> Bộ phận: {selectedBulletin.target_department}
                  </span>
                )}

                {selectedBulletin.target_type === 'specific' && (
                  <span className="tag-direct">
                    <i className="fas fa-user-tag"></i> Tin gửi riêng cho bạn
                  </span>
                )}

                {selectedBulletin.category === 'giao_viec' && selectedBulletin.task_status && (
                  <span className={`task-status-badge status-${selectedBulletin.task_status}`}>
                    <i className={TASK_STATUS_MAP[selectedBulletin.task_status]?.icon || 'fas fa-tasks'}></i>{' '}
                    {TASK_STATUS_MAP[selectedBulletin.task_status]?.label}
                  </span>
                )}
              </div>

              <button className="modal-close-btn" onClick={() => setIsDetailOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="detail-modal-body">
              <h1 className="detail-title">{selectedBulletin.title}</h1>

              <div className="detail-meta-bar">
                <div className="meta-item">
                  <i className="fas fa-user-tie"></i>
                  <span>Người đăng: <strong>{selectedBulletin.author_name}</strong></span>
                </div>
                <div className="meta-item">
                  <i className="fas fa-calendar-alt"></i>
                  <span>Thời gian: {new Date(selectedBulletin.created_at).toLocaleString('vi-VN')}</span>
                </div>
                {selectedBulletin.due_date && (
                  <div className="meta-item" style={{ color: '#b45309', fontWeight: 600 }}>
                    <i className="fas fa-calendar-check"></i>
                    <span>Hạn chót: <strong>{new Date(selectedBulletin.due_date).toLocaleString('vi-VN')}</strong></span>
                  </div>
                )}
                {selectedBulletin.is_read && selectedBulletin.read_at && (
                  <div className="meta-item text-success">
                    <i className="fas fa-check-circle"></i>
                    <span>Bạn đã xem lúc {new Date(selectedBulletin.read_at).toLocaleTimeString('vi-VN')}</span>
                  </div>
                )}
              </div>

              {/* Task workflow box in modal */}
              {selectedBulletin.category === 'giao_viec' && (
                <div className="detail-task-workflow-container">
                  {selectedBulletin.task_status === 'pending' && (
                    <div className="detail-task-box status-pending">
                      <div className="task-box-left">
                        <i className="fas fa-hourglass-start task-big-icon"></i>
                        <div>
                          <strong>Nhiệm vụ đang chờ người đảm nhiệm</strong>
                          <p>Bạn thuộc bộ phận được giao việc này? Hãy nhận nhiệm vụ để bắt đầu thực hiện.</p>
                        </div>
                      </div>
                      <button
                        className="btn-claim-task-big"
                        disabled={taskActionLoadingId === selectedBulletin.id}
                        onClick={() => handleClaimTask(selectedBulletin)}
                      >
                        {taskActionLoadingId === selectedBulletin.id ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-hand-paper"></i>
                        )}
                        <span>Nhận Nhiệm Vụ Này</span>
                      </button>
                    </div>
                  )}

                  {selectedBulletin.task_status === 'in_progress' && (
                    <>
                      {selectedBulletin.claimed_by_id === user?.id ? (
                        <div className="detail-task-box status-in-progress-mine">
                          <div className="task-box-left">
                            <i className="fas fa-user-check task-big-icon text-primary"></i>
                            <div>
                              <strong>Bạn đang đảm nhiệm nhiệm vụ này</strong>
                              <p>Sau khi thực hiện xong, vui lòng bấm "Báo cáo hoàn thành" để gửi kết quả cho Quản lý.</p>
                            </div>
                          </div>
                          <div className="task-box-actions-mine">
                            <button
                              className="btn-complete-task-big"
                              onClick={() => handleOpenCompleteModal(selectedBulletin)}
                            >
                              <i className="fas fa-check-circle"></i> Báo Cáo Hoàn Thành
                            </button>
                            <button
                              className="btn-unclaim-task-subtle"
                              disabled={taskActionLoadingId === selectedBulletin.id}
                              onClick={() => handleUnclaimTask(selectedBulletin)}
                            >
                              <i className="fas fa-undo"></i> Trả lại nhiệm vụ
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-task-box status-in-progress-other">
                          <i className="fas fa-user-clock task-big-icon"></i>
                          <div>
                            <strong>Nhiệm vụ đang được thực hiện</strong>
                            <p>Người đảm nhiệm: <strong>{selectedBulletin.claimed_by_name || 'Đồng nghiệp'}</strong></p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {selectedBulletin.task_status === 'completed' && (
                    <div className="detail-task-box status-completed">
                      <div className="task-box-left">
                        <i className="fas fa-check-double task-big-icon text-success"></i>
                        <div>
                          <strong>Nhiệm vụ đã được hoàn thành</strong>
                          <p>
                            Bởi <strong>{selectedBulletin.claimed_by_name || 'Nhân viên'}</strong>
                            {selectedBulletin.completed_at ? ` vào lúc ${new Date(selectedBulletin.completed_at).toLocaleString('vi-VN')}` : ''}
                          </p>
                        </div>
                      </div>
                      {selectedBulletin.completion_note && (
                        <div className="task-detail-completion-note">
                          <strong>Báo cáo kết quả:</strong> {selectedBulletin.completion_note}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedBulletin.summary && (
                <div className="detail-summary-highlight">{selectedBulletin.summary}</div>
              )}

              {/* Main Content with proper spacing */}
              <div className="detail-content-text">
                {selectedBulletin.content.split('\n').map((para, i) => (
                  <p key={i}>{para || '\u00A0'}</p>
                ))}
              </div>

              {/* External Video Section */}
              {selectedBulletin.video_url && (
                <div className="detail-video-wrapper">
                  <h4>
                    <i className="fas fa-video"></i> Video Hướng Dẫn:
                  </h4>
                  {selectedBulletin.video_url.includes('youtube.com') ||
                  selectedBulletin.video_url.includes('youtu.be') ? (
                    <div className="iframe-responsive">
                      <iframe
                        src={
                          selectedBulletin.video_url.includes('watch?v=')
                            ? selectedBulletin.video_url.replace('watch?v=', 'embed/')
                            : selectedBulletin.video_url.replace('youtu.be/', 'www.youtube.com/embed/')
                        }
                        title="YouTube video"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : (
                    <video controls className="html5-video">
                      <source src={selectedBulletin.video_url} />
                      Trình duyệt không hỗ trợ phát video.
                    </video>
                  )}
                </div>
              )}

              {/* Uploaded Video Section */}
              {selectedBulletin.media_files &&
                selectedBulletin.media_files.some((m) => m.mimeType.startsWith('video/')) && (
                  <div className="detail-video-wrapper">
                    <h4>
                      <i className="fas fa-play-circle"></i> Video Tải Lên:
                    </h4>
                    {selectedBulletin.media_files
                      .filter((m) => m.mimeType.startsWith('video/'))
                      .map((vid) => (
                        <div key={vid.fileId} className="video-card-player">
                          <video controls className="html5-video" preload="metadata">
                            <source src={vid.url} type={vid.mimeType} />
                            Trình duyệt không hỗ trợ phát video.
                          </video>
                          <span className="video-filename">{vid.originalName}</span>
                        </div>
                      ))}
                  </div>
                )}

              {/* Uploaded Image Gallery */}
              {selectedBulletin.media_files &&
                selectedBulletin.media_files.some((m) => m.mimeType.startsWith('image/')) && (
                  <div className="detail-gallery-wrapper">
                    <h4>
                      <i className="fas fa-images"></i> Hình Ảnh Đính Kèm (Nhấp để phóng to):
                    </h4>
                    <div className="gallery-grid">
                      {selectedBulletin.media_files
                        .filter((m) => m.mimeType.startsWith('image/'))
                        .map((img, idx, arr) => (
                          <div
                            key={img.fileId}
                            className="gallery-item"
                            onClick={() => handleOpenLightbox(arr, idx)}
                          >
                            <img src={img.url} alt={img.originalName} loading="lazy" />
                            <div className="gallery-overlay">
                              <i className="fas fa-search-plus"></i>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Acknowledgment Box */}
              {selectedBulletin.require_ack && (
                <div className="detail-ack-box">
                  <div className="ack-box-info">
                    <i className="fas fa-exclamation-circle ack-icon"></i>
                    <div>
                      <strong>Yêu cầu xác nhận:</strong>
                      <p>Bạn cần bấm xác nhận đã đọc và hiểu rõ nội dung thông báo / hướng dẫn này.</p>
                    </div>
                  </div>

                  {selectedBulletin.is_acknowledged ? (
                    <div className="ack-status-confirmed">
                      <i className="fas fa-check-double"></i>
                      <span>
                        Bạn đã xác nhận vào lúc{' '}
                        {selectedBulletin.acknowledged_at
                          ? new Date(selectedBulletin.acknowledged_at).toLocaleString('vi-VN')
                          : 'vừa xong'}
                      </span>
                    </div>
                  ) : (
                    <button
                      className="btn-confirm-ack"
                      onClick={handleAcknowledge}
                      disabled={isAckSubmitting}
                    >
                      {isAckSubmitting ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-check"></i>
                      )}
                      <span>Tôi đã đọc và hiểu rõ nội dung này</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="bulletin-modal-footer">
              <button className="btn-cancel" onClick={() => setIsDetailOpen(false)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COMPLETION NOTE MODAL */}
      {completionModalBulletin && (
        <div
          className="bulletin-modal-backdrop"
          style={{ zIndex: 1200 }}
          onClick={() => setCompletionModalBulletin(null)}
        >
          <div
            className="bulletin-modal-window completion-modal-window"
            style={{ maxWidth: '540px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bulletin-modal-header">
              <h2>
                <i className="fas fa-check-circle" style={{ color: '#16a34a', marginRight: '8px' }}></i>
                Báo Cáo Hoàn Thành Nhiệm Vụ
              </h2>
              <button className="modal-close-btn" onClick={() => setCompletionModalBulletin(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitCompleteTask} className="bulletin-form">
              <div className="bulletin-form-body" style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>
                    Nhiệm vụ:
                  </span>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', marginTop: '2px' }}>
                    {completionModalBulletin.title}
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>
                    Ghi chú / Kết quả công việc <span className="req">*</span>:
                  </label>
                  <textarea
                    rows={4}
                    value={completionNote}
                    onChange={(e) => setCompletionNote(e.target.value)}
                    placeholder="VD: Đã hoàn thiện xong 100 sản phẩm theo mẫu, đã kiểm tra đóng gói và bàn giao cho kho..."
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      resize: 'vertical',
                    }}
                  />
                  <small style={{ display: 'block', marginTop: '4px', color: '#64748b' }}>
                    Ghi chú này sẽ được lưu trữ và hiển thị cho Quản lý và các thành viên khác theo dõi.
                  </small>
                </div>
              </div>

              <div className="bulletin-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setCompletionModalBulletin(null)}
                  disabled={isCompleting}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  style={{ background: '#16a34a' }}
                  disabled={isCompleting}
                >
                  {isCompleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>
                      Đang lưu...
                    </>
                  ) : (
                    'Xác nhận hoàn thành'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LIGHTBOX FOR IMAGES */}
      {activeLightboxIndex !== null && lightboxImages.length > 0 && (
        <div className="lightbox-backdrop" onClick={() => setActiveLightboxIndex(null)}>
          <button className="lightbox-close-btn" onClick={() => setActiveLightboxIndex(null)}>
            <i className="fas fa-times"></i>
          </button>

          {activeLightboxIndex > 0 && (
            <button
              className="lightbox-nav-btn prev"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevLightbox();
              }}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
          )}

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImages[activeLightboxIndex].url}
              alt={lightboxImages[activeLightboxIndex].originalName}
              className="lightbox-main-img"
            />
            <div className="lightbox-caption">
              <span>{lightboxImages[activeLightboxIndex].originalName}</span>
              <span>
                {activeLightboxIndex + 1} / {lightboxImages.length}
              </span>
            </div>
          </div>

          {activeLightboxIndex < lightboxImages.length - 1 && (
            <button
              className="lightbox-nav-btn next"
              onClick={(e) => {
                e.stopPropagation();
                handleNextLightbox();
              }}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BulletinBoardPage;
